/**
 * Idempotenzschlüssel für die native Bridge (`[A-Za-z0-9._:-]{8,128}`). Gleicher Schlüssel =
 * derselbe Auftrag: Die App führt ihn höchstens einmal aus und liefert bei Wiederholung das
 * gespeicherte Ergebnis (`wiederholt`). Deshalb:
 *
 * - Automatischer Bondruck und automatische Lade beim Barverkauf hängen am Beleg – ein Beleg
 *   wird nie zweimal automatisch gedruckt, die Lade öffnet je Beleg höchstens einmal,
 *   auch nach Neuladen der Seite.
 * - Nachdruck und Taste „Schublade“ sind je Tastendruck ein neuer Auftrag (Zufallsteil).
 */

const MUSTER = /^[A-Za-z0-9._:-]{8,128}$/;

export function istGueltigerSchluessel(s: string): boolean {
  return MUSTER.test(s);
}

/** Erlaubte Zeichen je Teil (der Doppelpunkt trennt die Teile). */
function teil(text: string): string {
  return text.replace(/[^A-Za-z0-9._-]/g, '_');
}

/** FNV-1a (32 Bit) als Hex – nur zum Kürzen überlanger Schlüssel, keine Sicherheitsfunktion. */
function fnv1a(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

export function baueSchluessel(teile: readonly string[]): string {
  const roh = teile.map(teil).join(':');
  if (roh.length <= 128) {
    return roh.padEnd(8, '_');
  }
  // Eindeutig bleiben: Prüfsumme über den vollen Text anhängen
  return `${roh.slice(0, 119)}.${fnv1a(roh)}`;
}

export function zufallsTeil(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof c?.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Schlüssel des automatischen Drucks: genau einer je Beleg, für Original wie Kopie (Codex Runde
 * 3b, MITTEL 3). Der erste Versuch bindet in A1/A3 Schlüssel und Inhalt; jeder weitere automatische
 * Aufruf liefert dessen Ergebnis oder wird als `schluessel_anderer_inhalt` abgelehnt – nie ein
 * zweiter automatischer Ausdruck. Weitere Ausgaben nur als Nachdruck mit neuem Schlüssel.
 */
export function schluesselAutoDruck(kassenId: string, belegnummer: string): string {
  return baueSchluessel(['druck', kassenId, belegnummer, 'auto']);
}

export function schluesselNachdruck(
  kassenId: string,
  belegnummer: string,
  zufall: string = zufallsTeil(),
): string {
  return baueSchluessel(['druck', kassenId, belegnummer, zufall]);
}

export function schluesselLadeBeleg(kassenId: string, belegnummer: string): string {
  return baueSchluessel(['lade', kassenId, belegnummer]);
}

export function schluesselLadeManuell(kassenId: string, zufall: string = zufallsTeil()): string {
  return baueSchluessel(['lade', kassenId, 'manuell', zufall]);
}
